import { ImagePlus, Images, MapPin, Coins, Upload, X, Loader2, Info } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { fallbackImagenEvento, sanearUrlImagen } from "@/utils/imagenes";
import { subirImagenEvento, eliminarImagenEvento, MIME_ACCEPT } from "@/data/uploadsStore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { EVENTO_WIZARD } from "@/constants/testIds";
import {
  TIPOS_EVENTO,
  TIPO_ACCESO,
  TIPO_ACCESO_LABEL,
} from "@/data/adminEventosStore";
import { useSalonesOficiales } from "@/data/salonesCatalogoStore";

/**
 * Paso 1 · Información general.
 *
 * Cambios Fase B:
 *  - `imagenPrincipal` es OPCIONAL para guardar borrador y para avanzar
 *    entre pasos. Se marcará como obligatoria en el checklist del Paso 5.
 *  - Selección múltiple de salones (`salones: string[]`). El campo legacy
 *    `salon` se conserva por compatibilidad (primer salón seleccionado).
 *  - Precio por salón (uno por cada salón seleccionado) en el mismo Paso 1.
 *    El precio se aplica a todos los boletos del salón (no por mesa) y
 *    admite hasta dos decimales.
 */
export default function PasoInformacionGeneral({ valores, onChange, errores }) {
  const fileInputRef = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  // Catálogo oficial (fuente única backend). Los 9 salones seleccionables.
  const { salones: salonesOficiales, cargando: cargandoSalones } =
    useSalonesOficiales();

  const set = (campo) => (e) =>
    onChange(campo, e && e.target ? e.target.value : e);

  // Extrae la key del backend desde una URL relativa `/api/uploads/eventos/<hash>.ext`.
  const keyDesdeUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const idx = url.indexOf("/api/uploads/");
    return idx >= 0 ? url.slice(idx + "/api/uploads/".length) : "";
  };

  const abrirPicker = () => {
    if (subiendo) return;
    fileInputRef.current?.click();
  };

  const onFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSubiendo(true);
    const previousKey =
      valores.imagenPrincipalKey || keyDesdeUrl(valores.imagenPrincipal);
    const res = await subirImagenEvento(file, previousKey);
    setSubiendo(false);
    if (res?.error) {
      toast.error(res.error);
      return;
    }
    // Fuente de verdad: la key estable del storage. La URL es sólo para
    // previsualizar; el backend regenera una URL fresca al leer.
    onChange("imagenPrincipal", res.url);
    onChange("imagenPrincipalKey", res.key);
    toast.success("Imagen cargada");
  };

  const quitarImagen = async () => {
    if (subiendo) return;
    const previousKey =
      valores.imagenPrincipalKey || keyDesdeUrl(valores.imagenPrincipal);
    if (previousKey) {
      await eliminarImagenEvento(previousKey);
    }
    onChange("imagenPrincipal", "");
    onChange("imagenPrincipalKey", "");
  };

  const salonesSeleccionados = Array.isArray(valores.salones)
    ? valores.salones
    : [];
  const precios =
    valores.precios && typeof valores.precios === "object"
      ? valores.precios
      : {};

  const toggleSalon = (salon) => {
    const setSet = new Set(salonesSeleccionados);
    if (setSet.has(salon)) setSet.delete(salon);
    else setSet.add(salon);
    // Orden: históricos primero (preserva el salón histórico como principal
    // mientras el usuario no lo quite) + oficiales según el catálogo backend.
    const historicosSel = [...setSet].filter((s) => !salonesOficiales.includes(s));
    const oficialesSel = salonesOficiales.filter((s) => setSet.has(s));
    const arr = [...historicosSel, ...oficialesSel];
    onChange("salones", arr);
    // Compat: mantener `salon` como el primero seleccionado.
    onChange("salon", arr[0] || "");
    // Limpia precios de salones desmarcados.
    if (!setSet.has(salon) && precios[salon] !== undefined) {
      const copia = { ...precios };
      delete copia[salon];
      onChange("precios", copia);
    }
  };

  // Salones seleccionados que ya no pertenecen al catálogo oficial (legacy):
  // se conservan como "Salón histórico" y no se ofrecen a eventos nuevos.
  const historicosSeleccionados = salonesSeleccionados.filter(
    (s) => s && !salonesOficiales.includes(s),
  );

  const renderOpcionSalon = (s, historico = false) => {
    const marcado = salonesSeleccionados.includes(s);
    const checkboxId = EVENTO_WIZARD.fieldSalonCheckboxId(s);
    return (
      <label
        key={s}
        htmlFor={checkboxId}
        data-testid={EVENTO_WIZARD.fieldSalonOpcion(s)}
        className={`flex items-center gap-3 rounded-xl border bg-background px-4 py-3 cursor-pointer transition-colors ${historico ? "border-amber-500/50 hover:border-amber-500" : "hairline hover:border-border"}`}
      >
        <Checkbox
          id={checkboxId}
          data-testid={EVENTO_WIZARD.fieldSalonCheckbox(s)}
          checked={marcado}
          onCheckedChange={() => toggleSalon(s)}
          aria-label={`Seleccionar salón ${s}`}
        />
        <span className="text-sm flex-1">{s}</span>
        {historico && (
          <span
            data-testid={EVENTO_WIZARD.fieldSalonHistorico(s)}
            className="text-[10px] uppercase tracking-wide rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-0.5 whitespace-nowrap"
          >
            Salón histórico
          </span>
        )}
      </label>
    );
  };

  const setPrecio = (salon, raw) => {
    if (raw === "" || raw === null) {
      const copia = { ...precios };
      delete copia[salon];
      onChange("precios", copia);
      return;
    }
    // Hasta 2 decimales; number ≥ 0.
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) return;
    // Trunca a 2 decimales sin perder edición.
    const twoDec = Math.round(num * 100) / 100;
    onChange("precios", { ...precios, [salon]: twoDec });
  };

  return (
    <div
      data-testid={EVENTO_WIZARD.stepContent(1)}
      className="rounded-2xl border hairline bg-card p-6 md:p-8 flex flex-col gap-6"
    >
      <div>
        <span className="overline text-accent">Paso 1</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Información general
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Datos básicos del evento. La imagen es opcional para guardar
          borrador; será obligatoria al momento de publicar.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {/* Nombre */}
        <div className="flex flex-col gap-2 md:col-span-2">
          <Label htmlFor="wf-nombre">Nombre del evento *</Label>
          <Input
            id="wf-nombre"
            data-testid={EVENTO_WIZARD.fieldNombre}
            value={valores.nombre || ""}
            onChange={set("nombre")}
            placeholder="Ej. Gala Anual del Casino"
            className="bg-background"
          />
          {errores.nombre && (
            <p className="text-xs text-destructive">{errores.nombre}</p>
          )}
        </div>

        {/* Subtítulo */}
        <div className="flex flex-col gap-2 md:col-span-2">
          <Label htmlFor="wf-subtitulo">Subtítulo (opcional)</Label>
          <Input
            id="wf-subtitulo"
            data-testid={EVENTO_WIZARD.fieldSubtitulo}
            value={valores.subtitulo || ""}
            onChange={set("subtitulo")}
            placeholder="Una velada de gala"
            className="bg-background"
          />
        </div>

        {/* Descripción corta */}
        <div className="flex flex-col gap-2 md:col-span-2">
          <Label htmlFor="wf-descripcion">Descripción corta</Label>
          <Textarea
            id="wf-descripcion"
            data-testid={EVENTO_WIZARD.fieldDescripcion}
            value={valores.descripcionCorta || ""}
            onChange={set("descripcionCorta")}
            placeholder="Breve descripción visible en la app del socio…"
            rows={3}
            className="bg-background resize-none"
          />
        </div>

        {/* Imagen principal (upload al backend · abstracción ImageStorage) */}
        <div className="flex flex-col gap-2 md:col-span-2">
          <Label htmlFor="wf-imagen">
            Imagen principal{" "}
            <span className="text-muted-foreground text-xs font-normal">
              (JPG, PNG o WEBP · máx 10 MB · obligatoria al publicar)
            </span>
          </Label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-muted ring-1 ring-border/70 overflow-hidden grid place-items-center text-muted-foreground shrink-0">
              {valores.imagenPrincipal && valores.imagenPrincipal.trim() ? (
                <img
                  data-testid="wizard-imagen-preview"
                  src={sanearUrlImagen(valores.imagenPrincipal)}
                  alt="Vista previa"
                  className="w-full h-full object-cover"
                  onError={fallbackImagenEvento}
                />
              ) : (
                <ImagePlus className="w-6 h-6" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                ref={fileInputRef}
                data-testid={EVENTO_WIZARD.fieldImagen}
                id="wf-imagen"
                type="file"
                accept={MIME_ACCEPT}
                className="hidden"
                onChange={onFileChange}
              />
              <Button
                type="button"
                variant="outline"
                onClick={abrirPicker}
                disabled={subiendo}
                data-testid="wizard-imagen-subir"
              >
                {subiendo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {valores.imagenPrincipal ? "Reemplazar" : "Subir imagen"}
                  </>
                )}
              </Button>
              {valores.imagenPrincipal && !subiendo && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={quitarImagen}
                  data-testid="wizard-imagen-quitar"
                >
                  <X className="w-4 h-4 mr-2" />
                  Quitar
                </Button>
              )}
            </div>
          </div>
          {errores.imagenPrincipal && (
            <p className="text-xs text-destructive">{errores.imagenPrincipal}</p>
          )}
        </div>

        {/* Galería (placeholder) */}
        <div
          data-testid={EVENTO_WIZARD.fieldGaleria}
          className="flex flex-col gap-2 md:col-span-2"
        >
          <Label>Galería de imágenes</Label>
          <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 px-5 py-6 text-sm text-muted-foreground flex items-center gap-3">
            <Images className="w-5 h-5 text-accent" strokeWidth={1.6} />
            <span>
              Módulo de galería disponible en un próximo sprint (multi-carga y
              orden manual).
            </span>
          </div>
        </div>

        {/* Fecha */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="wf-fecha">Fecha *</Label>
          <Input
            id="wf-fecha"
            data-testid={EVENTO_WIZARD.fieldFecha}
            type="date"
            value={valores.fecha || ""}
            onChange={set("fecha")}
            className="bg-background"
          />
          {errores.fecha && (
            <p className="text-xs text-destructive">{errores.fecha}</p>
          )}
        </div>

        {/* Hora */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="wf-hora">Hora *</Label>
          <Input
            id="wf-hora"
            data-testid={EVENTO_WIZARD.fieldHora}
            type="time"
            value={valores.hora || ""}
            onChange={set("hora")}
            className="bg-background"
          />
          {errores.hora && (
            <p className="text-xs text-destructive">{errores.hora}</p>
          )}
        </div>

        {/* Salones (multi) */}
        <div
          className="flex flex-col gap-2 md:col-span-2"
          data-testid={EVENTO_WIZARD.fieldSalon}
        >
          <Label className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-accent" strokeWidth={1.7} />
            Salones del evento *
            <span className="text-muted-foreground text-xs font-normal">
              (uno o varios)
            </span>
          </Label>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            data-testid="wizard-field-salon-oficiales"
          >
            {salonesOficiales.map((s) => renderOpcionSalon(s, false))}
          </div>
          {cargandoSalones && salonesOficiales.length === 0 && (
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando catálogo de
              salones…
            </p>
          )}
          {historicosSeleccionados.length > 0 && (
            <div
              className="flex flex-col gap-2 mt-1"
              data-testid="wizard-field-salon-historicos"
            >
              <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5 leading-relaxed">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.8} />
                <span>
                  <b>Configuración histórica:</b> este evento usa un salón que ya no
                  forma parte del catálogo oficial. Se conserva tal cual; si lo
                  reemplazas, sólo podrás elegir un salón oficial.
                </span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {historicosSeleccionados.map((s) => renderOpcionSalon(s, true))}
              </div>
            </div>
          )}
          {errores.salones && (
            <p className="text-xs text-destructive">{errores.salones}</p>
          )}
        </div>

        {/* Precios por salón (visible cuando hay ≥1 salón) */}
        {salonesSeleccionados.length > 0 && (
          <div className="flex flex-col gap-2 md:col-span-2">
            <Label className="flex items-center gap-2">
              <Coins className="w-3.5 h-3.5 text-accent" strokeWidth={1.7} />
              Precio por salón *
              <span className="text-muted-foreground text-xs font-normal">
                (MXN · aplica a todos los boletos del salón)
              </span>
            </Label>
            <div className="flex flex-col gap-2">
              {salonesSeleccionados.map((s) => (
                <div
                  key={s}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border hairline bg-background px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{s}</p>
                    <p className="text-[11px] text-muted-foreground">
                      El precio se define por salón, no por mesa.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">$</span>
                    <Input
                      data-testid={EVENTO_WIZARD.fieldPrecioSalon(s)}
                      type="number"
                      min={0}
                      step={0.01}
                      value={
                        precios[s] === "" ||
                        precios[s] === null ||
                        precios[s] === undefined
                          ? ""
                          : precios[s]
                      }
                      onChange={(e) => setPrecio(s, e.target.value)}
                      placeholder="0.00"
                      className="w-36 bg-background text-right"
                    />
                    <span className="text-muted-foreground text-xs">MXN</span>
                  </div>
                </div>
              ))}
            </div>
            {errores.precios && (
              <p className="text-xs text-destructive">{errores.precios}</p>
            )}
          </div>
        )}

        {/* Tipo de evento */}
        <div className="flex flex-col gap-2">
          <Label>Tipo de evento</Label>
          <Select
            value={valores.tipoEvento || ""}
            onValueChange={(v) => onChange("tipoEvento", v)}
          >
            <SelectTrigger
              data-testid={EVENTO_WIZARD.fieldTipoEvento}
              className="bg-background"
            >
              <SelectValue placeholder="Selecciona el tipo" />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_EVENTO.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Edad mínima */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="wf-edad">Edad mínima</Label>
          <Input
            id="wf-edad"
            data-testid={EVENTO_WIZARD.fieldEdadMinima}
            type="number"
            min={0}
            max={99}
            value={valores.edadMinima ?? ""}
            onChange={(e) =>
              onChange(
                "edadMinima",
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            className="bg-background"
          />
        </div>

        {/* Tipo de acceso */}
        <div className="flex flex-col gap-2 md:col-span-2">
          <Label>Tipo de acceso</Label>
          <RadioGroup
            data-testid={EVENTO_WIZARD.fieldTipoAcceso}
            value={valores.tipoAcceso || TIPO_ACCESO.SOLO_SOCIOS}
            onValueChange={(v) => onChange("tipoAcceso", v)}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {[TIPO_ACCESO.SOLO_SOCIOS, TIPO_ACCESO.SOCIOS_INVITADOS].map((k) => (
              <label
                key={k}
                className="flex items-center gap-3 rounded-xl border hairline bg-background px-4 py-3 cursor-pointer hover:border-border transition-colors"
              >
                <RadioGroupItem value={k} id={`acc-${k}`} />
                <span className="text-sm">{TIPO_ACCESO_LABEL[k]}</span>
              </label>
            ))}
          </RadioGroup>
        </div>
      </div>
    </div>
  );
}
